import { cx } from '@emotion/css';
import { Button as ButtonSDS, Icon, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { Config } from '../../config';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logger, logUserEvent } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { AddToCart } from '../AddToCart';
import { ShopifyContext } from '../ShopifyProvider';
import { CartAccessory } from './CartAccessory';
import { CartItem } from './CartItem';
import { useCheckoutUrl, usePrice } from './hooks';
import { Price } from './Price';
import {
  activeWrapperCss,
  allAccessoriesCss,
  buttonWrapperCss,
  cartCloseButtonCss,
  cartCloseCss,
  cartInternalCss,
  cartOverlayActiveCss,
  cartOverlayCss,
  cartWrapperStyles,
  checkoutButtonCss,
  checkoutDisclaimerCss,
  featuredProductButtonCss,
  featuredProductImg,
  featuredProductPriceCss,
  featuredProductTitleCss,
  noCartItemsContainerCss,
  noCartItemsTitleCss,
  totalCss,
} from './styles';

export const Cart: FC = () => {
  const elementLocation = useContext(ElementLocation).elementLocation;
  const { formatMessage } = useContext(MessageContext);
  const { cart, products } = useContext(ShopifyContext);
  const featuredProductHandle = Config.shopify?.options.featuredProduct ?? '';
  const featuredProduct = products.shopify[featuredProductHandle];
  const featuredContentfulProduct = products.contentful[featuredProductHandle];
  const checkoutUrl = useCheckoutUrl();
  const lineItemVariantIds = Object.keys(cart.lineItems);
  const accessories = products.extras.accessories ?? [];
  const bundleItems = products.extras.bundleItems ?? [];
  const { variantMap } = products;

  const checkoutButtonCopy = formatMessage({
    id: 'cartCheckoutButton',
    defaultMessage: 'Checkout',
  });

  const disclaimer = formatMessage({
    id: 'cartCheckoutDisclaimer',
    defaultMessage: 'Shipping calculated at checkout',
  });

  const noCartItems = formatMessage({
    id: 'noCartItems',
    defaultMessage: 'Nothing added to cart yet',
  });

  // Resolve handle from variant ID via variantMap for accessory/bundle checks
  const getHandle = (variantId: string): string => variantMap[variantId]?.handle ?? variantId;
  const isAccessory = (variantId: string): boolean => accessories.includes(getHandle(variantId));
  const isBundleItem = (variantId: string): boolean => bundleItems.includes(getHandle(variantId));
  const hasItems = lineItemVariantIds.length > 0;

  const totalFromCart = lineItemVariantIds.reduce((acc, variantId) => {
    const entry = variantMap[variantId];

    if (entry && !isBundleItem(variantId)) {
      return acc + entry.variant.price * (cart.lineItems[variantId] ?? 0);
    }

    return acc;
  }, 0);

  const total = usePrice(totalFromCart);

  const totalCopy = formatMessage(
    {
      id: 'cartTotal',
      defaultMessage: 'Total {total}',
    },
    { total }
  );

  // Log the checkout interaction and take the user to the Shopify site.
  const onCheckoutClick = (): void => {
    logUserEvent({
      eventAction: UserAction.Click,
      eventCategory: 'CheckoutButton',
      context: { targetUrl: checkoutUrl.href, elementText: checkoutButtonCopy, elementLocation },
    });
    void logger.flush();
  };

  return (
    <div
      className={cx(cartWrapperStyles(hasItems), {
        [activeWrapperCss]: cart?.cartOpen,
      })}
    >
      <div
        onClick={() => cart.setCartOpen(false)}
        className={cx(cartOverlayCss, {
          [cartOverlayActiveCss]: cart?.cartOpen,
        })}
      />
      <div className={buttonWrapperCss}>
        <button className={cartCloseCss} onClick={() => cart.setCartOpen(false)}>
          <Icon name="cross" className={cartCloseButtonCss} />
        </button>
      </div>
      <div className={cartInternalCss} />
      {lineItemVariantIds.map(variantId => {
        const entry = variantMap[variantId];

        if (!entry || isAccessory(variantId) || isBundleItem(variantId)) return null;

        const product = products.shopify[entry.handle];

        if (!product) return null;

        return (
          <CartItem
            key={variantId}
            {...product}
            price={entry.variant.price}
            sku={entry.variant.sku}
            id={entry.variant.id}
            variantTitle={entry.variant.title}
          />
        );
      })}
      <div className={allAccessoriesCss}>
        {accessories.map(
          productHandle =>
            products.shopify[productHandle] &&
            products.contentful[productHandle] && (
              <CartAccessory
                key={productHandle}
                {...products.shopify[productHandle]!}
                title={products.contentful[productHandle]!.title}
              />
            )
        )}
      </div>
      {!hasItems && (
        <div className={noCartItemsContainerCss}>
          <div className={noCartItemsTitleCss}>{noCartItems}</div>
          {featuredProduct && featuredContentfulProduct && (
            <>
              <div className={featuredProductImg(featuredProduct.image)} />
              <div className={featuredProductTitleCss}>{featuredContentfulProduct.title}</div>
              <Price
                className={featuredProductPriceCss}
                productPrice={featuredProduct?.price ?? 0}
              />
              <AddToCart
                className={featuredProductButtonCss}
                productHandle={featuredProductHandle}
              />
            </>
          )}
        </div>
      )}
      {hasItems && (
        <>
          <div className={totalCss}>{totalCopy}</div>
          <div className={checkoutButtonCss}>
            <ButtonSDS link={checkoutUrl.href} type="Primary" onClick={onCheckoutClick}>
              {checkoutButtonCopy}
            </ButtonSDS>
          </div>
          <div className={checkoutDisclaimerCss}>{disclaimer}</div>
        </>
      )}
    </div>
  );
};
